import type { Localized } from '../types/funnel';

export interface HelpAnswer {
  q: Localized;
  a: Localized;
}

/** Short contextual answers per screen. Keys match screen ids used in the funnel. */
export const helpAnswers: Record<string, HelpAnswer[]> = {
  home: [
    {
      q: { en: 'Is this a fixed price?', es: '¿Es un precio fijo?' },
      a: {
        en: 'No. We bill by the hour for the actual time worked. You’ll see an itemized estimate before you commit to anything.',
        es: 'No. Cobramos por hora según el tiempo real trabajado. Verá un estimado detallado antes de comprometerse.',
      },
    },
    {
      q: { en: 'Can I talk to someone?', es: '¿Puedo hablar con alguien?' },
      a: {
        en: 'Yes. Our AI-assisted phone line answers 24/7 at (656) 268-0800 and can stay with you while you finish online. For a person on our team, call (813) 851-0590.',
        es: 'Sí. Nuestra línea con asistencia de IA contesta 24/7 al (656) 268-0800 y puede acompañarle mientras termina en línea. Para hablar con una persona del equipo, llame al (813) 851-0590.',
      },
    },
  ],
  stepSize: [
    {
      q: { en: 'Do I need to know everything now?', es: '¿Necesito saberlo todo ahora?' },
      a: { en: 'No. Pick what’s closest — you can change any answer later and the estimate updates.', es: 'No. Elija lo más cercano — puede cambiar cualquier respuesta y el estimado se actualiza.' },
    },
  ],
  stepAddresses: [
    {
      q: { en: 'What if I’m outside Tampa?', es: '¿Y si estoy fuera de Tampa?' },
      a: {
        en: 'We serve Tampa and nearby communities within our service area. If your route is outside it, we keep your details and our team reviews options with you.',
        es: 'Atendemos Tampa y comunidades cercanas dentro de nuestra área. Si su ruta está fuera, guardamos sus datos y nuestro equipo revisa opciones con usted.',
      },
    },
  ],
  stepAccess: [
    {
      q: { en: 'Do you charge for stairs?', es: '¿Cobran por escaleras?' },
      a: {
        en: 'There’s no separate stair fee in this estimate. Stairs, elevators and long walks take more time, which is billed hourly — so telling us now keeps the estimate accurate.',
        es: 'No hay un cargo separado por escaleras en este estimado. Escaleras, elevadores y caminatas largas toman más tiempo, que se cobra por hora — por eso conviene indicarlo ahora.',
      },
    },
  ],
  stepInventory: [
    {
      q: { en: 'Not sure about every item?', es: '¿No está seguro de cada artículo?' },
      a: { en: 'Add what you know and tick “I’m not sure yet.” Our team will confirm the list with you before move day.', es: 'Agregue lo que sepa y marque “Aún no estoy seguro”. Nuestro equipo confirmará la lista con usted antes de la mudanza.' },
    },
  ],
  stepPacking: [
    {
      q: { en: 'If I pack myself, what do I save?', es: 'Si empaco yo, ¿cuánto ahorro?' },
      a: {
        en: 'You skip the optional packing charges. Our packing prices are set by our team, so we’ll show the exact savings once those prices are on your estimate.',
        es: 'Se ahorra los cargos opcionales de empaque. Nuestro equipo fija esos precios; le mostraremos el ahorro exacto cuando estén en su estimado.',
      },
    },
    {
      q: { en: 'Is packing included?', es: '¿El empaque está incluido?' },
      a: {
        en: 'Your crew wraps and protects furniture as part of the move. Packing boxes, cartons and materials are optional paid services.',
        es: 'Su equipo envuelve y protege los muebles como parte de la mudanza. Empacar cajas, cartones y materiales son servicios opcionales con costo.',
      },
    },
  ],
  stepDate: [
    {
      q: { en: 'I’m not sure of the date', es: 'No estoy seguro de la fecha' },
      a: { en: 'That’s fine. We’ll save your plan without holding a truck or asking for a date-specific deposit.', es: 'Está bien. Guardamos su plan sin apartar un camión ni pedir un depósito para una fecha específica.' },
    },
  ],
  stepContact: [
    {
      q: { en: 'I need to check with my spouse', es: 'Necesito consultarlo con mi pareja' },
      a: {
        en: 'Your answers are saved on this device. Once you send your request, our team can share your exact plan so you can decide together.',
        es: 'Sus respuestas se guardan en este dispositivo. Al enviar su solicitud, nuestro equipo puede compartir su plan exacto para que decidan juntos.',
      },
    },
  ],
  stepEstimate: [
    {
      q: { en: 'Why the travel hour?', es: '¿Por qué la hora de traslado?' },
      a: { en: 'It covers the crew and truck driving from our base to you and back. It’s one separate billed hour, apart from your moving time.', es: 'Cubre el viaje del equipo y el camión desde nuestra base hasta usted y de regreso. Es una hora facturada aparte de su tiempo de mudanza.' },
    },
    {
      q: { en: 'Why the operating surcharge?', es: '¿Por qué el recargo operativo?' },
      a: {
        en: 'The 20% operating surcharge covers costs like truck use, wear and applicable tolls. We show its amount once its calculation is confirmed on your estimate.',
        es: 'El recargo operativo del 20% cubre costos como uso y desgaste del camión y peajes aplicables. Mostramos el monto cuando su cálculo se confirme en su estimado.',
      },
    },
    {
      q: { en: 'Can I choose two movers?', es: '¿Puedo elegir dos trabajadores?' },
      a: {
        en: 'Yes, you can ask for two. If we recommend three, it’s for safe handling and a faster day. Our team confirms two can do the job safely before your move.',
        es: 'Sí, puede pedir dos. Si recomendamos tres, es por manejo seguro y un día más rápido. Nuestro equipo confirma que dos pueden hacerlo con seguridad antes de la mudanza.',
      },
    },
    {
      q: { en: 'Will there be more charges?', es: '¿Habrá más cargos?' },
      a: {
        en: 'Everything known is listed here: minimum, travel, surcharge and any extras you choose. Your final bill reflects the actual time worked.',
        es: 'Todo lo conocido aparece aquí: mínimo, traslado, recargo y extras que elija. Su factura final refleja el tiempo real trabajado.',
      },
    },
  ],
  stepReserve: [
    {
      q: { en: 'Do I have to pay everything now?', es: '¿Tengo que pagar todo ahora?' },
      a: { en: 'No. A $99 deposit reserves your move and is credited to your final bill. The rest is settled after the move based on actual time.', es: 'No. Un depósito de $99 reserva su mudanza y se acredita a su factura final. El resto se liquida después según el tiempo real.' },
    },
    {
      q: { en: 'I’m not the payer', es: 'No soy quien paga' },
      a: { en: 'Choose “Someone else is paying.” The cardholder signs their own authorization — you never sign for them.', es: 'Elija “Otra persona paga”. El titular de la tarjeta firma su propia autorización — usted nunca firma por esa persona.' },
    },
  ],
};
